import React, { useMemo } from 'react';
import { MdOutlinePeopleAlt } from 'react-icons/md';
import { SectionCard, StatTile, SubHeading } from './ui';
import ChartFrame from '../charts/ChartFrame';
import BarChart from '../charts/BarChart';
import {
  fmtCompact, fmtNum, fmtPct, fmtMoney, fmtSigned, latestYearRecord, compareToReference, safeArray, isNum, DASH,
} from '../format';

/** "$75,000 to $99,999" -> "$75–100K"; "Less than $5,000" -> "< $5K"; "$200,000 or more" -> "$200K+". */
export function shortIncomeLabel(label) {
  if (!label) return DASH;
  const nums = (label.match(/\$?[\d,]+/g) || []).map((s) => Number(s.replace(/[$,]/g, ''))).filter((n) => Number.isFinite(n));
  const k = (n) => (n >= 1000 ? `${Math.round((n + 1) / 1000)}K` : String(n));
  if (/less than/i.test(label) && nums.length) return `< $${k(nums[0])}`;
  if (/or more/i.test(label) && nums.length) return `$${k(nums[0])}+`;
  if (nums.length >= 2) return `$${k(nums[0])}–${k(nums[1])}`;
  return label;
}

function deltaVs(value, reference, { digits = 0, suffix = '', invert = false, absolute = false } = {}) {
  if (!isNum(value) || !isNum(reference)) return null;
  if (absolute) {
    const diff = value - reference;
    const tone = Math.abs(diff) < 0.5 ? 'flat' : (diff > 0) !== invert ? 'up' : 'down';
    return { label: `${fmtSigned(diff, digits, suffix)} vs US`, tone };
  }
  const cmp = compareToReference(value, reference);
  if (!cmp) return null;
  const tone = cmp.tone === 'flat' ? 'flat' : (cmp.tone === 'up') !== invert ? 'up' : 'down';
  return { label: cmp.label, tone };
}

export default function DemographicsCard({ data, status }) {
  const summary = data?.summary || {};
  const national = summary.national_reference || {};
  const scope = data?.scope || {};

  const age = useMemo(() => {
    const rec = latestYearRecord(data?.age_distribution);
    if (!rec) return null;
    const brackets = safeArray(rec.brackets);
    return {
      year: rec.year,
      categories: brackets.map((b) => b.bracket.replace(/\s+years?/i, '').replace(' and over', '+').replace('Under ', '<')),
      local: brackets.map((b) => (isNum(b.share_percent) ? b.share_percent : null)),
      nationalShares: brackets.map((b) => (isNum(b.national_share_percent) ? b.national_share_percent : null)),
      rows: brackets.map((b) => ({ bracket: b.bracket, count: b.count, share: b.share_percent, national: b.national_share_percent, index: b.index_vs_national })),
    };
  }, [data]);

  const income = useMemo(() => {
    const owner = latestYearRecord(data?.household_income?.owner);
    const renter = latestYearRecord(data?.household_income?.renter);
    if (!owner && !renter) return null;
    const base = safeArray((owner || renter).brackets);
    const renterMap = new Map(safeArray(renter?.brackets).map((b) => [b.bracket, b]));
    const ownerMap = new Map(safeArray(owner?.brackets).map((b) => [b.bracket, b]));
    return {
      year: (owner || renter).year,
      categories: base.map((b) => shortIncomeLabel(b.bracket)),
      owner: base.map((b) => { const o = ownerMap.get(b.bracket); return o && isNum(o.share_percent) ? o.share_percent : null; }),
      renter: base.map((b) => { const r = renterMap.get(b.bracket); return r && isNum(r.share_percent) ? r.share_percent : null; }),
      rows: base.map((b) => {
        const o = ownerMap.get(b.bracket); const r = renterMap.get(b.bracket);
        return { bracket: b.bracket, ownerCount: o?.count, ownerShare: o?.share_percent, renterCount: r?.count, renterShare: r?.share_percent };
      }),
    };
  }, [data]);

  const subtitle = data
    ? `${scope.radius_miles || DASH} mi radius · ${fmtNum(scope.intersected_tract_count)} census tracts · area-weighted ACS estimates${summary.population?.year ? ` · ${summary.population.year}` : ''}`
    : null;

  const pop = summary.population || {};
  const mobility = summary.mobility || {};
  const households = summary.households || {};
  const mhi = summary.median_household_income_usd || {};
  const natMhi = national.median_household_income_usd || {};

  return (
    <SectionCard id="le-section-demographics" icon={<MdOutlinePeopleAlt size={20} />} title="Demographics" subtitle={subtitle} status={status} dataset={data}>
      {data ? (
        <>
          <div className="le-stat-grid">
            <StatTile label="Population" value={fmtCompact(pop.latest)} sub={isNum(pop.cagr_percent) ? `${fmtSigned(pop.cagr_percent, 1, '%')}/yr growth` : null} emphasis />
            <StatTile label="Median age" value={isNum(summary.median_age_years) ? `${summary.median_age_years.toFixed(1)} yrs` : DASH} delta={deltaVs(summary.median_age_years, national.median_age_years, { digits: 1, suffix: ' yrs', absolute: true })} />
            <StatTile label="Owner median income" value={fmtMoney(mhi.owner, { compact: true })} delta={deltaVs(mhi.owner, natMhi.owner)} />
            <StatTile label="Renter median income" value={fmtMoney(mhi.renter, { compact: true })} delta={deltaVs(mhi.renter, natMhi.renter)} />
            <StatTile label="Renter share" value={fmtPct(households.renter_share_percent, 0)} delta={deltaVs(households.renter_share_percent, national.renter_share_percent, { suffix: ' pts', absolute: true })} sub={`${fmtCompact(households.owner)} own · ${fmtCompact(households.renter)} rent`} />
            <StatTile label="Renters earning $75K+" value={fmtCompact(summary.renter_households_income_75k_plus)} sub="Conversion pool (households)" hint="Renter households with income of $75,000 or more — the buyers-in-waiting pool" />
            <StatTile label="Avg household size" value={isNum(summary.average_household_size) ? summary.average_household_size.toFixed(2) : DASH} sub="People per household" />
            <StatTile label="Moved in past year" value={fmtPct(mobility.moved_past_year_percent, 1)} delta={deltaVs(mobility.moved_past_year_percent, national.moved_past_year_percent, { digits: 1, suffix: ' pts', absolute: true })} sub={isNum(mobility.inflow_from_outside_county_percent) ? `${fmtPct(mobility.inflow_from_outside_county_percent, 1)} arrived from outside the county` : null} />
            <StatTile label="Ages 30–44" value={fmtPct(summary.population_age_30_44_share_percent, 1)} sub="Prime household-formation share" />
          </div>

          {age ? (
            <>
              <SubHeading>Age distribution · {age.year}</SubHeading>
              <ChartFrame
                subtitle="Share of population by age bracket · gray tick = US share"
                legend={[{ id: 'local', label: 'Within radius', color: 'var(--le-s1)' }, { id: 'us', label: 'United States', color: 'var(--le-ctx)', kind: 'line' }]}
                table={{
                  columns: [
                    { key: 'bracket', label: 'Age' },
                    { key: 'count', label: 'People', align: 'right', format: (v) => fmtNum(v) },
                    { key: 'share', label: 'Share', align: 'right', format: (v) => fmtPct(v, 2) },
                    { key: 'national', label: 'US share', align: 'right', format: (v) => fmtPct(v, 2) },
                    { key: 'index', label: 'Index vs US', align: 'right', format: (v) => (isNum(v) ? v.toFixed(2) : DASH) },
                  ],
                  rows: age.rows,
                }}
              >
                <BarChart
                  categories={age.categories}
                  series={[{ id: 'local', label: 'Within radius', color: 'var(--le-s1)', values: age.local }]}
                  reference={{ label: 'United States', values: age.nationalShares }}
                  yFormat={(v) => `${v}%`}
                  valueFormat={(v) => fmtPct(v, 1)}
                  height={190}
                />
              </ChartFrame>
            </>
          ) : null}

          {income ? (
            <>
              <SubHeading>Household income by tenure · {income.year}</SubHeading>
              <ChartFrame
                subtitle="Share of owner vs renter households in each income bracket"
                legend={[{ id: 'owner', label: 'Owner households', color: 'var(--le-s1)' }, { id: 'renter', label: 'Renter households', color: 'var(--le-s2)' }]}
                table={{
                  columns: [
                    { key: 'bracket', label: 'Income' },
                    { key: 'ownerCount', label: 'Owner HH', align: 'right', format: (v) => fmtNum(v) },
                    { key: 'ownerShare', label: 'Owner share', align: 'right', format: (v) => fmtPct(v, 2) },
                    { key: 'renterCount', label: 'Renter HH', align: 'right', format: (v) => fmtNum(v) },
                    { key: 'renterShare', label: 'Renter share', align: 'right', format: (v) => fmtPct(v, 2) },
                  ],
                  rows: income.rows,
                }}
              >
                <BarChart
                  categories={income.categories}
                  series={[
                    { id: 'owner', label: 'Owner households', color: 'var(--le-s1)', values: income.owner },
                    { id: 'renter', label: 'Renter households', color: 'var(--le-s2)', values: income.renter },
                  ]}
                  yFormat={(v) => `${v}%`}
                  valueFormat={(v) => fmtPct(v, 1)}
                  height={190}
                  labelRotate
                />
              </ChartFrame>
            </>
          ) : null}

          <div className="le-footnotes muted">
            {scope.aggregation_method ? `Method: ${scope.aggregation_method.replace(/_/g, ' ')}` : null}
            {scope.boundary_vintage ? ` · ${scope.boundary_vintage} tract boundaries` : null}
            {national.method ? ` · National reference: ${national.method.replace(/_/g, ' ')}` : null}
          </div>
        </>
      ) : null}
    </SectionCard>
  );
}
